/**
 * Screenshots Tab Content Component
 *
 * Displays all screenshots for an issue in table view with View/Delete actions.
 * Shows source (Manual vs Agent), linked test IDs, and upload metadata.
 *
 * Module: 3.0 - System Operations (Issues Manager Enhancement)
 * PRD: docs/prds/PRD-ISSUES-MANAGER.md v2.2
 */

import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest, getAuthHeaders } from "@/lib/queryClient";
import type { IssueAttachment } from "@shared/schema";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Eye, Trash2, ImageIcon, Upload, Bot, Link2, User } from "lucide-react";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { ScreenshotUploadField } from "@/components/testing/screenshot-upload-field";

interface ScreenshotsTabContentProps {
  issueId: string;
  screenshots: IssueAttachment[];
  isLoading?: boolean;
}

const SOURCE_CONFIG = {
  manual_upload: {
    label: "Manual",
    variant: "default" as const,
    icon: User,
    color: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
  },
  design_review_agent: {
    label: "Design Review",
    variant: "secondary" as const,
    icon: Bot,
    color: "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200",
  },
  qa_review_agent: {
    label: "QA Review",
    variant: "secondary" as const,
    icon: Bot,
    color: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
  },
  playwright_mcp: {
    label: "Playwright",
    variant: "outline" as const,
    icon: Link2,
    color: "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200",
  },
  bulk_import: {
    label: "Imported",
    variant: "outline" as const,
    icon: Upload,
    color: "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200",
  },
};

export function ScreenshotsTabContent({
  issueId,
  screenshots,
  isLoading,
}: ScreenshotsTabContentProps) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedScreenshot, setSelectedScreenshot] = useState<IssueAttachment | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const deleteMutation = useMutation({
    mutationFn: async (screenshotId: string) => {
      const res = await apiRequest("DELETE", `/api/crm/issues/${issueId}/screenshots/${screenshotId}`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/crm/issues", issueId, "screenshots"] });
      toast({
        title: "Screenshot deleted",
        description: "The screenshot has been removed from this issue.",
      });
      setDeleteConfirmOpen(false);
      setDeletingId(null);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete screenshot",
        variant: "destructive",
      });
    },
  });

  const handleView = (screenshot: IssueAttachment) => {
    setSelectedScreenshot(screenshot);
    setLightboxOpen(true);
  };

  const handleDeleteClick = (screenshotId: string) => {
    setDeletingId(screenshotId);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = () => {
    if (deletingId) {
      deleteMutation.mutate(deletingId);
    }
  };

  const handleUploadClick = () => {
    setUploadDialogOpen(true);
  };

  const handleUploadSubmit = async () => {
    if (!uploadFile) return;

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", uploadFile);
      formData.append("captureType", "manual_upload");
      formData.append("description", "Screenshot uploaded from Screenshots tab");

      const authHeaders = await getAuthHeaders();
      const response = await fetch(`/api/crm/issues/${issueId}/screenshots/upload`, {
        method: "POST",
        headers: authHeaders,
        credentials: "include",
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Failed to upload screenshot");
      }

      queryClient.invalidateQueries({ queryKey: ["/api/crm/issues", issueId, "screenshots"] });

      toast({
        title: "Screenshot uploaded",
        description: "The screenshot has been added to this issue.",
      });

      setUploadFile(null);
      setUploadDialogOpen(false);
    } catch (error: any) {
      toast({
        title: "Upload failed",
        description: error.message || "Failed to upload screenshot",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const deletingScreenshot = screenshots.find((s) => s.id === deletingId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-sm text-muted-foreground">Loading screenshots...</p>
      </div>
    );
  }

  // Empty state rendering - must still include dialogs
  if (screenshots.length === 0) {
    return (
      <>
        <div className="flex flex-col items-center justify-center py-16">
          <ImageIcon className="h-16 w-16 text-muted-foreground mb-4" />
          <p className="text-lg font-medium mb-2">No screenshots attached</p>
          <p className="text-sm text-muted-foreground mb-6">
            Upload screenshots to provide visual evidence for this issue.
          </p>
          <Button onClick={handleUploadClick}>
            <Upload className="h-4 w-4 mr-2" />
            Upload Screenshot
          </Button>
        </div>

        {/* Upload Screenshot Dialog - Required for empty state upload */}
        <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
          <DialogContent className="sm:max-w-[500px]" aria-describedby="upload-screenshot-description-empty">
            <DialogTitle>Upload Screenshot</DialogTitle>
            <DialogDescription id="upload-screenshot-description-empty">
              Add a screenshot to provide visual evidence for this issue.
            </DialogDescription>

            <div className="space-y-4 py-4">
              <ScreenshotUploadField
                value={uploadFile}
                onChange={(files) => setUploadFile(Array.isArray(files) ? files[0] ?? null : files)}
                label="Screenshot"
                required={true}
                disabled={isUploading}
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setUploadDialogOpen(false);
                  setUploadFile(null);
                }}
                disabled={isUploading}
              >
                Cancel
              </Button>
              <Button onClick={handleUploadSubmit} disabled={!uploadFile || isUploading}>
                {isUploading ? "Uploading..." : "Upload"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return (
    <>
      <div className="space-y-4">
        {/* Header with Upload Button */}
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium">All Screenshots ({screenshots.length})</h3>
          <Button onClick={handleUploadClick} size="sm">
            <Upload className="h-4 w-4 mr-2" />
            Upload Screenshot
          </Button>
        </div>

        {/* Table */}
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="w-[80px]">Preview</TableHead>
                <TableHead className="w-[200px]">Filename</TableHead>
                <TableHead className="w-[140px]">Source</TableHead>
                <TableHead className="w-[120px]">Linked Test</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="w-[140px]">Uploaded</TableHead>
                <TableHead className="w-[100px] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {screenshots.map((screenshot) => {
                const sourceConfig = SOURCE_CONFIG[screenshot.source] || SOURCE_CONFIG.manual_upload;
                const SourceIcon = sourceConfig.icon;

                return (
                  <TableRow key={screenshot.id} className="hover:bg-muted/50">
                    {/* Thumbnail */}
                    <TableCell>
                      <div
                        className="w-12 h-12 rounded overflow-hidden border cursor-pointer hover:ring-2 hover:ring-primary"
                        onClick={() => handleView(screenshot)}
                      >
                        <img
                          src={screenshot.publicUrl || `/api/crm/issues/${issueId}/screenshots/${screenshot.id}/download`}
                          alt={screenshot.fileName}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </TableCell>

                    {/* Filename */}
                    <TableCell>
                      <button
                        onClick={() => handleView(screenshot)}
                        className="text-sm font-medium hover:underline text-left truncate max-w-[180px] block"
                        title={screenshot.fileName}
                      >
                        {screenshot.fileName}
                      </button>
                      <p className="text-xs text-muted-foreground">
                        {(screenshot.fileSize / 1024).toFixed(2)} KB
                      </p>
                    </TableCell>

                    {/* Source */}
                    <TableCell>
                      <Badge variant={sourceConfig.variant} className={`text-xs ${sourceConfig.color}`}>
                        <SourceIcon className="w-3 h-3 mr-1" />
                        {sourceConfig.label}
                      </Badge>
                    </TableCell>

                    {/* Linked Test ID */}
                    <TableCell>
                      {screenshot.linkedTestId ? (
                        <code className="text-xs bg-muted px-1.5 py-0.5 rounded font-mono">
                          {screenshot.linkedTestId}
                        </code>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>

                    {/* Description */}
                    <TableCell>
                      {screenshot.description ? (
                        <p
                          className="text-sm truncate max-w-[200px]"
                          title={screenshot.description}
                        >
                          {screenshot.description}
                        </p>
                      ) : (
                        <span className="text-muted-foreground text-sm">—</span>
                      )}
                    </TableCell>

                    {/* Date Uploaded */}
                    <TableCell className="text-sm">
                      {format(new Date(screenshot.uploadedAt), "MMM d, yyyy")}
                      <p className="text-xs text-muted-foreground">
                        {format(new Date(screenshot.uploadedAt), "HH:mm")}
                      </p>
                    </TableCell>

                    {/* Actions */}
                    <TableCell>
                      <div className="flex gap-1 justify-end">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleView(screenshot)}
                          title="View screenshot"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteClick(screenshot.id)}
                          title="Delete screenshot"
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>

        {/* Summary */}
        <div className="flex items-center justify-between text-sm text-muted-foreground px-2">
          <p>
            {screenshots.length} screenshot{screenshots.length !== 1 ? "s" : ""} (
            {(screenshots.reduce((sum, s) => sum + (s.fileSize || 0), 0) / 1024 / 1024).toFixed(2)}{" "}
            MB total)
          </p>
        </div>
      </div>

      {/* Screenshot Lightbox */}
      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden" aria-describedby="screenshot-description">
          <DialogTitle className="sr-only">
            Screenshot Preview: {selectedScreenshot?.fileName || "Issue Screenshot"}
          </DialogTitle>
          <DialogDescription id="screenshot-description" className="sr-only">
            Full-size preview of {selectedScreenshot?.fileName || "screenshot"} uploaded on{" "}
            {selectedScreenshot ? format(new Date(selectedScreenshot.createdAt), "MMM d, yyyy HH:mm") : ""}
          </DialogDescription>
          {selectedScreenshot && (
            <div className="space-y-4">
              {/* Screenshot Image */}
              <div className="flex items-center justify-center bg-muted rounded-md">
                <img
                  src={selectedScreenshot.publicUrl || `/api/crm/issues/${issueId}/screenshots/${selectedScreenshot.id}/download`}
                  alt={selectedScreenshot.fileName}
                  className="max-w-full max-h-[70vh] object-contain"
                />
              </div>

              {/* Screenshot Metadata */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <ImageIcon className="w-4 h-4 text-muted-foreground" />
                  <p className="font-medium text-sm">{selectedScreenshot.fileName}</p>
                </div>

                <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                  <div>
                    <p className="font-semibold">Uploaded</p>
                    <p>{format(new Date(selectedScreenshot.createdAt), "MMM d, yyyy HH:mm")}</p>
                  </div>
                  <div>
                    <p className="font-semibold">Size</p>
                    <p>{(selectedScreenshot.fileSize / 1024).toFixed(2)} KB</p>
                  </div>
                  <div>
                    <p className="font-semibold">Source</p>
                    <p className="capitalize">{selectedScreenshot.source.replace(/_/g, " ")}</p>
                  </div>
                  {selectedScreenshot.linkedTestId && (
                    <div>
                      <p className="font-semibold">Linked Test</p>
                      <p className="font-mono">{selectedScreenshot.linkedTestId}</p>
                    </div>
                  )}
                  {selectedScreenshot.description && (
                    <div className="col-span-2">
                      <p className="font-semibold">Description</p>
                      <p>{selectedScreenshot.description}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Screenshot</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deletingScreenshot?.fileName}"? This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeletingId(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Upload Screenshot Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="sm:max-w-[500px]" aria-describedby="upload-screenshot-description">
          <DialogTitle>Upload Screenshot</DialogTitle>
          <DialogDescription id="upload-screenshot-description">
            Add a screenshot to provide visual evidence for this issue.
          </DialogDescription>

          <div className="space-y-4 py-4">
            <ScreenshotUploadField
              value={uploadFile}
              onChange={(files) => setUploadFile(Array.isArray(files) ? files[0] ?? null : files)}
              label="Screenshot"
              required={true}
              disabled={isUploading}
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setUploadDialogOpen(false);
                setUploadFile(null);
              }}
              disabled={isUploading}
            >
              Cancel
            </Button>
            <Button onClick={handleUploadSubmit} disabled={!uploadFile || isUploading}>
              {isUploading ? "Uploading..." : "Upload"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
